import { useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import { initialQuestionIds } from '@aldebaran/common/constants/kb';
import { RootState } from '../types/graph';
import { useUpdateQuestionnaire } from './questionnaire.hooks';
import { useQuestionnairePages } from './useQuestionnairePages';

const DEBOUNCE_DELAY = 15000;

const INITIAL_QUESTION_IDS = new Set(Object.values(initialQuestionIds));

const debouncedSave = debounce(
  (saveFn: () => void) => {
    saveFn();
  },
  DEBOUNCE_DELAY,
  { leading: true, trailing: false },
);

export const useAutoSaveOnVisibilityChange = () => {
  const { isLastPage } = useQuestionnairePages();
  const { updateQuestionnaire } = useUpdateQuestionnaire({
    complete: isLastPage,
  });

  const questionnaireId = useSelector(
    (state: RootState) => state.questionnaireId,
  );
  // Nothing to save until the patient has answered a questionnaire question on
  // this device: an earlier save would replace the stored answers (a kiosk
  // check-in continued on the phone, ALDO-2759) with onboarding data only.
  // Latched, so clearing that answer afterwards still gets saved.
  const hasQuestionnaireAnswers = useSelector((state: RootState) =>
    Object.entries(state.userResponses).some(
      ([questionId, response]) =>
        !INITIAL_QUESTION_IDS.has(questionId) &&
        Object.keys(response).length > 0,
    ),
  );
  const hasAnsweredOnDevice = useRef(false);
  if (hasQuestionnaireAnswers) hasAnsweredOnDevice.current = true;

  useEffect(() => {
    return () => {
      debouncedSave.cancel();
    };
  }, []);

  const handleVisibilityChange = useCallback(() => {
    if (
      document.visibilityState === 'hidden' &&
      questionnaireId &&
      hasAnsweredOnDevice.current
    ) {
      debouncedSave(updateQuestionnaire);
    }
  }, [questionnaireId, updateQuestionnaire]);

  useEffect(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [handleVisibilityChange]);
};
